<script setup>
import { slugify } from '@/utils/slugify';
import { Link } from '@inertiajs/vue3';
import { useI18n } from 'vue-i18n';
import { route } from 'ziggy-js';
const { t, locale } = useI18n();

const props = defineProps({
    product: { type: Object, required: true },
});

function truncateText(text, length) {
    if (!text) return '';
    const clean = text.replace(/<[^>]+>/g, '');
    return clean.length > length ? clean.substring(0, length) + '...' : clean;
}

function buyNow(e) {
    e.preventDefault();
    e.stopPropagation();
    if (props.product.shopier_url) {
        window.open(props.product.shopier_url, '_blank', 'noopener');
    }
}
</script>

<template>
    <div class="group relative flex flex-col overflow-hidden rounded-lg border border-transparent bg-white/80 shadow-lg backdrop-blur-md transition-all duration-500 hover:-translate-y-2 hover:border-blue-500/40 hover:shadow-2xl dark:bg-gray-800/70">
        <Link :href="route(locale === 'tr' ? 'products.show-client.tr' : 'products.show-client.en', { id: product.id, title: slugify(product.title), locale })" class="flex flex-1 flex-col">
            <!-- Üst şerit -->
            <div v-if="product.top_title" class="rounded-lg-3xl absolute left-0 top-0 z-30 w-full overflow-hidden px-2 py-[4px] text-center text-[0.7rem] font-bold tracking-wide text-white shadow-lg" :style="{ backgroundColor: product.top_title_bg_color || '#ff006e', boxShadow: '0 0 15px ' + (product.top_title_bg_color || '#ff006e') }">
                <div v-if="product.is_scrolling_top_title" class="marquee">
                    <span>{{ product.top_title }}</span>
                </div>
                <div v-else class="truncate">{{ product.top_title }}</div>
            </div>

            <!-- Ürün resmi -->
            <div class="rounded-lg-3xl relative h-56 w-full overflow-hidden md:h-72">
                <img v-if="product.attachments?.length > 0" v-lazy="`/storage/${product.attachments[0].path}`" :alt="product.title" class="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" loading="lazy" />
                <div v-else class="flex h-full w-full items-center justify-center bg-gray-700 text-gray-400">{{ t('app.noImage') }}</div>

                <!-- Fiyat Badge -->
                <span class="absolute bottom-3 right-3 z-40 flex flex-col items-end rounded-lg border border-white/20 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 px-3 py-1 text-sm font-bold text-white shadow-xl backdrop-blur-md">
                    <span v-if="product.discount_price" class="text-xs font-normal line-through opacity-80"> {{ Number(product.discount_price).toLocaleString() }} {{ t('app.tl') }} </span>
                    <span> {{ Number(product.price).toLocaleString() }} {{ t('app.tl') }} </span>
                </span>

                <!-- Hover Overlay -->
                <div class="absolute inset-0 z-20 flex items-center justify-center bg-gradient-to-t from-black/60 via-black/30 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                    <span class="rounded-lg border border-white/20 bg-white/10 px-5 py-2 text-sm font-semibold text-white shadow-lg backdrop-blur-lg">
                        {{ t('app.seeDetail') }}
                    </span>
                </div>
            </div>

            <!-- İçerik -->
            <div class="flex flex-grow flex-col p-3 pb-2 text-gray-900 dark:text-gray-200 sm:p-5 sm:pb-3">
                <h3 class="mb-2 text-sm font-bold leading-snug transition-colors duration-300 group-hover:text-blue-500 sm:text-base md:text-lg lg:text-lg">
                    {{ truncateText(product.title, 40) }}
                </h3>
            </div>
        </Link>

        <!-- Aksiyon butonu -->
        <div class="px-3 pb-3 sm:px-5 sm:pb-5">
            <button
                v-if="product.shopier_url"
                @click="buyNow"
                type="button"
                class="flex w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 px-3 py-2.5 text-xs font-bold text-white shadow-lg transition hover:from-purple-700 hover:to-pink-700 focus:outline-none focus:ring-2 focus:ring-purple-400 sm:gap-2 sm:px-4 sm:text-sm"
                aria-label="Hemen Satın Al"
            >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5 flex-shrink-0 sm:h-4 sm:w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Hemen Satın Al
            </button>

            <button
                v-else
                disabled
                type="button"
                class="flex w-full cursor-not-allowed items-center justify-center gap-1.5 whitespace-nowrap rounded-lg bg-gray-300 px-3 py-2.5 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-400 sm:gap-2 sm:px-4 sm:text-sm"
                aria-label="Yakında"
            >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5 flex-shrink-0 sm:h-4 sm:w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Yakında Satışta
            </button>
        </div>
    </div>
</template>

<style scoped>
.marquee {
    position: relative;
    white-space: nowrap;
    overflow: hidden;
}
.marquee span {
    display: inline-block;
    animation: marquee 6s linear infinite;
}
@keyframes marquee {
    0% {
        transform: translateX(100%);
    }
    100% {
        transform: translateX(-100%);
    }
}
</style>
